import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Download,
  FileText,
  Mail,
  Pencil,
  Send,
  Sparkles,
  Upload,
  UsersRound,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { fileToDataUrl } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";
import { useClients, useUsers } from "@/lib/hooks";
import { roleLabel } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY_PROFILE = {
  headline: "",
  introduction: "",
  whatWeDo: "",
  services: [],
  presentationText: "",
};

function MemberOverview({ member, open, onOpenChange, tasks, events, clients }) {
  if (!member) return null;
  const activeTasks = tasks.filter((task) => (
    (task.assigneeIds || []).includes(member.id) && task.status !== "CONCLUIDO"
  ));
  const upcomingEvents = events
    .filter((event) => (event.assigneeIds || []).includes(member.id) && event.date >= format(new Date(), "yyyy-MM-dd"))
    .sort((first, second) => `${first.date}${first.startTime}`.localeCompare(`${second.date}${second.startTime}`));
  const managedClients = clients.filter((client) => (
    (client.associateIds || []).includes(member.id)
    || client.accountManagerId === member.id
    || client.trafficManagerId === member.id
  ));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="sr-only">Perfil de {member.name}</DialogTitle>
          <DialogDescription className="sr-only">Visão geral do colaborador.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center text-center">
          <UserAvatar user={member} size="h-28 w-28" className="ring-4 ring-primary/15 [&_img]:object-top" />
          <h2 className="mt-4 font-poppins text-2xl font-bold">{member.name}</h2>
          <div className="mt-1 text-sm font-medium text-primary">{roleLabel(member)}</div>
          <a href={`mailto:${member.email}`} className="mt-1 text-xs text-muted-foreground hover:text-primary">{member.email}</a>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-muted/40 p-3 text-center"><div className="font-poppins text-xl font-bold">{activeTasks.length}</div><div className="text-[10px] uppercase text-muted-foreground">Tarefas ativas</div></div>
          <div className="rounded-xl bg-muted/40 p-3 text-center"><div className="font-poppins text-xl font-bold">{upcomingEvents.length}</div><div className="text-[10px] uppercase text-muted-foreground">Próximos eventos</div></div>
          <div className="rounded-xl bg-muted/40 p-3 text-center"><div className="font-poppins text-xl font-bold">{managedClients.length}</div><div className="text-[10px] uppercase text-muted-foreground">Clientes</div></div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><CalendarDays className="h-4 w-4 text-primary" /> Próximo compromisso</h3>
            {upcomingEvents[0] ? (
              <>
                <div className="text-sm font-medium">{upcomingEvents[0].title}</div>
                <div className="mt-1 text-xs text-muted-foreground capitalize">
                  {format(parseISO(upcomingEvents[0].date), "EEEE, d MMM", { locale: pt })}
                  {!upcomingEvents[0].allDay && ` às ${upcomingEvents[0].startTime}`}
                </div>
              </>
            ) : <p className="text-xs text-muted-foreground">Sem compromissos próximos.</p>}
          </div>
          <div className="rounded-xl border border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><BriefcaseBusiness className="h-4 w-4 text-primary" /> Contas acompanhadas</h3>
            {managedClients.length ? (
              <div className="flex flex-wrap gap-1.5">{managedClients.slice(0, 5).map((client) => <Badge key={client.id} variant="secondary" className="rounded-full text-[10px]">{client.name}</Badge>)}</div>
            ) : <p className="text-xs text-muted-foreground">Sem clientes diretamente associados.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompanyEditor({ open, onOpenChange, profile }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [servicesText, setServicesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [newPdf, setNewPdf] = useState(null); // { name, format, dataUrl }

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_PROFILE, ...profile });
    setServicesText((profile?.services || []).join("\n"));
    setNewPdf(null);
  }, [open, profile]);

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const onPdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Escolha um ficheiro PDF"); return; }
    const dataUrl = await fileToDataUrl(file);
    setNewPdf({ name: file.name, format: file.type, dataUrl });
    event.target.value = "";
  };

  const removePdf = async () => {
    try {
      await api.delete("/company/presentation-pdf");
      queryClient.invalidateQueries({ queryKey: ["company-profile"] });
      setNewPdf(null);
      toast.success("Apresentação removida");
    } catch {
      toast.error("Não foi possível remover a apresentação");
    }
  };

  const currentPdfName = newPdf?.name || profile?.presentationPdf?.name;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        services: servicesText.split("\n").map((service) => service.trim()).filter(Boolean),
      };
      if (newPdf) payload.presentationPdf = newPdf;
      else delete payload.presentationPdf;
      await api.put("/company", payload);
      queryClient.invalidateQueries({ queryKey: ["company-profile"] });
      toast.success("Guardado");
      onOpenChange(false);
    } catch {
      toast.error("Não foi possível guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-poppins">Editar página da I AM Social</DialogTitle>
          <DialogDescription>Os textos aparecem nesta página. A apresentação para enviar por email é o PDF que carregares abaixo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-border p-4">
            <Label className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Apresentação (PDF para email)</Label>
            <p className="mt-1 text-xs text-muted-foreground">Carrega o PDF já pronto. É este ficheiro que os botões "PDF" e "Enviar apresentação" usam.</p>
            {currentPdfName ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 truncate">{currentPdfName}</span>
                {newPdf && <Badge variant="secondary" className="rounded-full text-[10px]">novo</Badge>}
                <button type="button" onClick={newPdf ? () => setNewPdf(null) : removePdf} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">Ainda não há PDF — usa-se uma apresentação gerada automaticamente.</p>
            )}
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary hover:underline">
              <Upload className="h-3.5 w-3.5" /> {currentPdfName ? "Substituir PDF" : "Carregar PDF"}
              <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} />
            </label>
          </div>
          <div><Label>Frase principal</Label><Input value={form.headline} onChange={(event) => set("headline", event.target.value)} className="mt-1.5" /></div>
          <div><Label>Apresentação</Label><Textarea value={form.introduction} onChange={(event) => set("introduction", event.target.value)} className="mt-1.5 min-h-[95px]" /></div>
          <div><Label>O que fazemos?</Label><Textarea value={form.whatWeDo} onChange={(event) => set("whatWeDo", event.target.value)} className="mt-1.5 min-h-[95px]" /></div>
          <div><Label>Serviços</Label><Textarea value={servicesText} onChange={(event) => setServicesText(event.target.value)} className="mt-1.5 min-h-[130px]" placeholder="Um serviço por linha" /><p className="mt-1 text-xs text-muted-foreground">Coloca um serviço por linha.</p></div>
          <div><Label>Texto de apresentação da equipa</Label><Textarea value={form.presentationText} onChange={(event) => set("presentationText", event.target.value)} className="mt-1.5 min-h-[95px]" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90">{saving ? "A guardar..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IAMSocial() {
  const { user } = useAuth();
  const { data: users = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const [selectedMember, setSelectedMember] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { data: profile } = useQuery({
    queryKey: ["company-profile"],
    queryFn: async () => (await api.get("/company")).data,
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", "company-overview"],
    queryFn: async () => (await api.get("/tasks")).data,
  });
  const { data: events = [] } = useQuery({
    queryKey: ["events", "company-overview"],
    queryFn: async () => (await api.get("/events")).data,
  });
  const activeUsers = users.filter((member) => member.status !== "INATIVO");
  const canEdit = ["ADMIN", "SUB_ADMIN"].includes(user.role);

  const sharePresentation = async () => {
    setSharing(true);
    try {
      const { data } = await api.get("/company/presentation.pdf", { responseType: "blob" });
      const file = new File([data], "apresentacao_i_am_social.pdf", { type: "application/pdf" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: "Apresentação I AM Social",
          text: "Conhece a I AM Social, o que fazemos e a nossa equipa.",
          files: [file],
        });
        return;
      }

      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      URL.revokeObjectURL(url);
      window.location.href = "mailto:?subject=Apresentação%20I%20AM%20Social&body=Olá%2C%0A%0ASegue%20a%20apresentação%20da%20I%20AM%20Social.%20O%20ficheiro%20PDF%20foi%20descarregado%20e%20está%20pronto%20para%20ser%20anexado.%0A%0AObrigado.";
      toast.info("O PDF foi descarregado. Anexa-o ao email que acabou de abrir.");
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Não foi possível preparar a apresentação");
    } finally {
      setSharing(false);
    }
  };

  const downloadPresentation = async () => {
    try {
      const { data } = await api.get("/company/presentation.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "apresentacao_i_am_social.pdf";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível descarregar a apresentação");
    }
  };

  if (!profile) return <p className="text-sm text-muted-foreground">A carregar a I AM Social...</p>;

  return (
    <div>
      <PageHeader
        title="I AM Social"
        subtitle="A nossa equipa, a nossa forma de trabalhar e aquilo que criamos juntos."
        actions={
          <div className="flex flex-wrap gap-2">
            {canEdit && <Button variant="outline" onClick={() => setEditorOpen(true)}><Pencil className="mr-1.5 h-4 w-4" /> Editar página</Button>}
            <Button variant="outline" onClick={downloadPresentation}><Download className="mr-1.5 h-4 w-4" /> Ver PDF</Button>
            <Button onClick={sharePresentation} disabled={sharing} className="bg-primary text-black hover:bg-primary/90"><Send className="mr-1.5 h-4 w-4" /> {sharing ? "A preparar..." : "Enviar apresentação"}</Button>
          </div>
        }
      />

      <Card className="relative mb-7 overflow-hidden border-0 bg-[#212529] p-7 text-white lg:p-10">
        <div className="relative z-10 max-w-3xl">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary"><Sparkles className="h-4 w-4" /> We are your business mirror!</div>
          <h2 className="font-poppins text-2xl font-bold leading-tight lg:text-4xl">{profile.headline}</h2>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-white/65 lg:text-base">{profile.introduction}</p>
        </div>
        <div className="absolute -right-20 -top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      </Card>

      <section className="mb-10">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h2 className="font-poppins text-xl font-bold">A equipa</h2>
            <p className="mt-1 text-sm text-muted-foreground">Clica num perfil para conhecer o trabalho de cada pessoa.</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><UsersRound className="h-4 w-4" /> {activeUsers.length} pessoas</div>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {activeUsers.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedMember(member)}
              className="group overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-md"
            >
              <div className="relative flex h-44 items-center justify-center overflow-hidden bg-gradient-to-br from-primary/15 via-muted/30 to-background">
                {member.avatarUrl ? (
                  <img src={member.avatarUrl} alt={member.name} className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <UserAvatar user={member} size="h-24 w-24" />
                )}
                <div className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-primary shadow"><ArrowRight className="h-4 w-4" /></div>
              </div>
              <div className="p-4">
                <div className="truncate font-poppins font-semibold">{member.name}</div>
                <div className="mt-1 truncate text-xs text-muted-foreground">{roleLabel(member)}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">O que fazemos?</div>
          <h2 className="font-poppins text-2xl font-bold">Estratégia e criatividade com propósito.</h2>
          <p className="mt-4 text-sm leading-7 text-muted-foreground">{profile.whatWeDo}</p>
          {profile.presentationText && <p className="mt-4 border-l-2 border-primary pl-4 text-sm leading-6 text-foreground/80">{profile.presentationText}</p>}
        </div>
        <Card className="border-border p-5 lg:p-6">
          <div className="mb-4 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" /><h3 className="font-poppins font-semibold">Áreas de especialização</h3></div>
          <div className="grid gap-2 sm:grid-cols-2">
            {(profile.services || []).map((service) => (
              <div key={service} className="flex items-center gap-2 rounded-xl border border-border bg-muted/20 px-3 py-3 text-sm"><span className="h-2 w-2 rounded-full bg-primary" />{service}</div>
            ))}
          </div>
          <Button onClick={sharePresentation} disabled={sharing} className="mt-5 w-full bg-primary text-black hover:bg-primary/90"><Mail className="mr-2 h-4 w-4" /> Enviar esta apresentação por email</Button>
        </Card>
      </section>

      <MemberOverview member={selectedMember} open={Boolean(selectedMember)} onOpenChange={(open) => !open && setSelectedMember(null)} tasks={tasks} events={events} clients={clients} />
      <CompanyEditor open={editorOpen} onOpenChange={setEditorOpen} profile={profile} />
    </div>
  );
}
